import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { normalizeMachineName } from "layouts/machine/inventoryList";

const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });

const paginationOptions = {
  showCount: false,
  alignRight: true,
  compact: true,
  shortPageLabel: true,
  iconNavigation: true,
  grayIconNavigation: true,
  pageSize: 25,
  showPageSizeSelector: false,
  hideNavigationOnSinglePage: true,
};

const depositoColumnWidths = ["30%", "50%", "20%"];
const desechosColumnWidths = ["35%", "65%"];

function sortValue(machine, column) {
  switch (column) {
    case "MachineType":
      return machine.MachineType?.Type;
    case "Machine":
      return `${machine.SerialNumber ?? ""} · ${machine.Name ?? ""}`;
    default:
      return "";
  }
}

export default function LocalMachineTable({ local, query, makeTable, actions, isLoading }) {
  const [sort, setSort] = useState(null);
  const [resetPageKey, setResetPageKey] = useState(0);
  const machines = Array.isArray(local.Machines) ? local.Machines : [];
  const normalizedQuery = normalizeMachineName(query);

  const visibleMachines = useMemo(() => {
    const filtered = normalizedQuery
      ? machines.filter((machine) =>
          [machine.Name, machine.SerialNumber, machine.MachineType?.Type].some((value) =>
            normalizeMachineName(value).includes(normalizedQuery)
          )
        )
      : machines;

    if (!sort) return filtered;

    const direction = sort.direction === "desc" ? -1 : 1;
    return filtered
      .map((machine, index) => ({ machine, index }))
      .sort((left, right) => {
        const result = collator.compare(
          String(sortValue(left.machine, sort.column) ?? ""),
          String(sortValue(right.machine, sort.column) ?? "")
        );
        return result * direction || left.index - right.index;
      })
      .map(({ machine }) => machine);
  }, [machines, normalizedQuery, sort]);

  useEffect(() => {
    setResetPageKey((key) => key + 1);
  }, [query]);

  const { columns, rows } = makeTable(visibleMachines);
  const sortableColumns = columns.map((column) => {
    if (column.accessor === "Actions") return column;
    const label = column.Header;
    return {
      ...column,
      Header: (
        <MDBox display="flex" alignItems="center" justifyContent="flex-start" gap={0.25}>
          <span>{label}</span>
          <MDBox display="flex">
            {["asc", "desc"].map((direction) => (
              <IconButton
                key={direction}
                size="small"
                aria-label={`Ordenar ${label} ${direction === "asc" ? "ascendente" : "descendente"} en ${local.Name}`}
                aria-pressed={sort?.column === column.accessor && sort.direction === direction}
                onClick={(event) => {
                  event.stopPropagation();
                  setSort({ column: column.accessor, direction });
                  setResetPageKey((key) => key + 1);
                }}
                sx={{
                  padding: 0,
                  width: 24,
                  height: 22,
                  color:
                    sort?.column === column.accessor && sort.direction === direction
                      ? "info.main"
                      : "text.secondary",
                }}
              >
                {direction === "asc" ? (
                  <ArrowDropUpIcon fontSize="small" />
                ) : (
                  <ArrowDropDownIcon fontSize="small" />
                )}
              </IconButton>
            ))}
          </MDBox>
        </MDBox>
      ),
    };
  });

  return (
    <MDBox mb={4}>
      <MDBox
        mx={2}
        mb={0.5}
        px={1.5}
        py={1.25}
        borderRadius="md"
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        flexWrap="wrap"
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1}
        sx={({ palette }) => ({
          minWidth: 0,
          backgroundColor: palette.mode === "dark" ? "#263E5B" : "#CCE2F7",
        })}
      >
        <MDTypography
          variant="h6"
          color="inherit"
          fontWeight="bold"
          sx={({ palette }) => ({
            fontSize: "20px",
            color: palette.mode === "dark" ? "#E4F0FC" : "#174A78",
          })}
        >
          {`${local.Name || "Local sin nombre"} · ${machines.length} ${machines.length === 1 ? "unidad" : "unidades"}`}
        </MDTypography>
        <MDBox display="flex" alignItems="center">
          {actions}
        </MDBox>
      </MDBox>
      <DataTable
        table={{ columns: sortableColumns, rows }}
        isLoading={isLoading}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        resultsPagination={rows.length > 25}
        disablePagination={rows.length <= 25}
        hideFooter={rows.length <= 25}
        resultsPaginationOptions={paginationOptions}
        fixedColumnWidths={
          columns.some((column) => column.accessor === "Actions")
            ? depositoColumnWidths
            : desechosColumnWidths
        }
        resetPageKey={resetPageKey}
        emptyMessage="Sin resultados"
        noEndBorder
      />
    </MDBox>
  );
}

LocalMachineTable.propTypes = {
  local: PropTypes.object.isRequired,
  query: PropTypes.string.isRequired,
  makeTable: PropTypes.func.isRequired,
  actions: PropTypes.node,
  isLoading: PropTypes.bool.isRequired,
};
